import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Building2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Layout } from '@/components/wiki/Layout';
import { useSEO } from '@/hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const category = searchParams.get('category') || '';
  const [searchQuery, setSearchQuery] = useState(query);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dynamic SEO for search
  useSEO({
    title: category ? `${category}` : (query ? `Search: ${query}` : 'Search'),
    description: category 
      ? `Browse all ${category} on Handshake - the business encyclopedia wiki.`
      : (query 
        ? `Search results for "${query}" on Handshake. Find companies, executives, investors, and startups.`
        : 'Search Handshake business wiki. Find companies, executives, investors, and startup information.'),
  });

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      try {
        const params = { limit: 100 };
        if (query) params.search = query;
        if (category) params.category = category;
        
        const response = await axios.get(`${API}/pages`, { params });
        setResults(response.data);
      } catch (error) {
        console.error('Error fetching results:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [query, category]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim() });
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="search-results-page">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#54595d]" />
            <Input
              type="search"
              placeholder="Search Handshake..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-24 py-5 text-base rounded-sm bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d] focus:border-[#36c]"
              data-testid="search-input"
            />
            <Button 
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[#36c] hover:bg-[#2a4b8d] text-white font-medium"
              data-testid="search-btn"
            >
              Search
            </Button>
          </div>
        </form>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            {category ? (
              <h1 className="font-serif text-2xl font-semibold text-[#202122]">
                {category}
              </h1>
            ) : query ? (
              <h1 className="font-serif text-2xl font-semibold text-[#202122]">
                Results for "{query}"
              </h1>
            ) : (
              <h1 className="font-serif text-2xl font-semibold text-[#202122]">
                All Pages
              </h1>
            )}
            <p className="text-[#54595d] mt-1 text-sm">
              {loading ? 'Searching...' : `${results.length} results found`}
            </p>
          </div>
          <Link to="/create">
            <Button className="bg-[#36c] hover:bg-[#2a4b8d] text-white font-medium" data-testid="create-page-btn">
              Create New Page
            </Button>
          </Link>
        </div>

        {/* Results */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white rounded-sm p-5 border border-[#c8ccd1] animate-pulse">
                <div className="h-5 bg-[#eaecf0] rounded w-1/3 mb-3" />
                <div className="h-4 bg-[#eaecf0] rounded w-full mb-2" />
                <div className="h-4 bg-[#eaecf0] rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-3">
            {results.map((page) => (
              <Link
                key={page.id}
                to={`/wiki/${page.slug}`}
                className={`block rounded-sm p-5 border transition-colors ${
                  page.is_sponsored 
                    ? 'bg-[#fef6e7] border-[#fc3] hover:border-[#36c]' 
                    : 'bg-white border-[#c8ccd1] hover:border-[#36c]'
                }`}
                data-testid={`result-${page.slug}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="font-medium text-[#36c] hover:underline">
                        {page.title}
                      </h2>
                      {page.is_sponsored && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-[#fc3] text-[#202122]">
                          <Sparkles className="h-3 w-3" />
                          Sponsored
                        </span>
                      )}
                    </div>
                    {page.summary && (
                      <p className="text-[#54595d] text-sm line-clamp-2">
                        {page.summary}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-sm p-12 text-center border border-[#c8ccd1]">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-[#72777d]" />
            <h3 className="font-medium text-[#202122] mb-2">
              No results found
            </h3>
            <p className="text-[#54595d] mb-6 text-sm">
              {query ? `No pages match "${query}". Would you like to create one?` : 'No pages have been created yet.'}
            </p>
            <Link to="/create">
              <Button className="bg-[#36c] hover:bg-[#2a4b8d] text-white font-medium" data-testid="create-first-btn">
                Create "{query || 'New Page'}"
              </Button>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
